import React, { useState, useEffect } from 'react';
import { useConfig } from '../context/ConfigContext';
import api from '../api';

// Default content (fallback)
//
// FIGURE-FREE AND HISTORY-FREE ON PURPOSE. See the note on DEFAULT_HOME_CONTENT
// in Home.js: this is published on behalf of a business whose terms this file
// does not know, it is free text with no interpolation step, and it must be kept
// byte-identical to the copies in server/server.js and PageContentSettings.js.
//
// `timeline` is empty rather than neutral. Every other field can be phrased
// without asserting anything specific, but a company history cannot: "2020 - The
// Beginning - Founded with a simple vision" is a dated factual claim about a
// company, and there is no vague-but-true version of a founding year. The four
// dated entries that used to sit here were one business's story, published as
// the story of every tenant that had not written their own About page. An empty
// timeline hides the Our Story section entirely (see the render below), which is
// the same convention the homepage already uses for unconfigured financing.
const DEFAULT_ABOUT_CONTENT = {
  heroSubtitle: 'Making land ownership accessible to everyone through flexible financing and transparent service.',
  missionStatement: "We believe land ownership shouldn't be limited to those with perfect credit or large cash reserves. Our mission is to help everyday Americans achieve their dream of owning land through accessible financing options and exceptional customer service.",
  values: [
    { id: 1, icon: '🤝', title: 'Transparency', description: 'No hidden fees, no surprises. We believe in clear communication and honest business practices.' },
    { id: 2, icon: '💪', title: 'Accessibility', description: 'We work with customers regardless of credit score. Everyone deserves a chance at land ownership.' },
    { id: 3, icon: '🎯', title: 'Simplicity', description: 'Our process is straightforward. Browse, choose, and purchase. All online with no complicated paperwork.' },
    { id: 4, icon: '🌟', title: 'Support', description: "We're here for you throughout your land ownership journey, from purchase to payoff." }
  ],
  timeline: [],
  offerings: [
    { id: 1, icon: '🏞️', title: 'Quality Land', description: 'We hand-select every property in our portfolio, ensuring it offers real value.' },
    { id: 2, icon: '💳', title: 'Flexible Financing', description: 'Multiple down payment options and loan terms to fit your budget.' },
    { id: 3, icon: '📱', title: 'Online Dashboard', description: 'Manage your loan, make payments, view documents, and track your progress online 24/7.' },
    { id: 4, icon: '✅', title: 'No Credit Checks', description: "We don't run credit checks. Approval is based on your ability to make payments." },
    { id: 5, icon: '📄', title: 'Clear Contracts', description: "Easy-to-understand contracts for deed with no legal jargon." }
  ]
};

function AboutUs() {
  const { getCompanyLegalName } = useConfig();
  const [aboutContent, setAboutContent] = useState(DEFAULT_ABOUT_CONTENT);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await api.get('/config/page-content');
        if (response.data.aboutContent) {
          setAboutContent(response.data.aboutContent);
        }
      } catch (err) {
        console.error('Failed to fetch page content:', err);
      }
    };
    fetchContent();
  }, []);

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero" style={{ minHeight: '300px' }}>
        <div className="hero-content">
          <h1 className="hero-title">About {getCompanyLegalName()}</h1>
          <p className="hero-subtitle">
            {aboutContent.heroSubtitle}
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="features">
        <div className="container">
          <h2 className="section-title">Our Mission</h2>
          <p style={{
            textAlign: 'center',
            maxWidth: '800px',
            margin: '0 auto 3rem',
            fontSize: '1.1rem',
            lineHeight: '1.8',
            color: 'var(--color-text-light)'
          }}>
            At {getCompanyLegalName()}, {aboutContent.missionStatement}
          </p>

          {/* Values Cards */}
          <div className="features-grid">
            {aboutContent.values.map((value) => (
              <div key={value.id} className="feature-card">
                <div className="feature-icon">{value.icon}</div>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story
          Hidden entirely when the tenant has no timeline entries, rather than
          publishing a heading over an empty column or, as before, over another
          company's founding dates. Same rule as the homepage financing section:
          no story is better than someone else's story. */}
      {(aboutContent.timeline || []).length > 0 && (
      <section className="how-it-works">
        <div className="container">
          <h2 className="section-title">Our Story</h2>

          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            {aboutContent.timeline.map((item) => (
              <div key={item.id} className="step">
                <div className="step-number">{item.year}</div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* What We Offer */}
      <section className="financing">
        <div className="container">
          <h2 className="section-title">What We Offer</h2>

          <div className="features-grid">
            {aboutContent.offerings.map((offering) => (
              <div key={offering.id} className="feature-card">
                <div className="feature-icon">{offering.icon}</div>
                <h3>{offering.title}</h3>
                <p>{offering.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="cta-content">
          <h2>Ready to Start Your Land Ownership Journey?</h2>
          <p>Browse our available properties and take the first step toward owning your piece of land.</p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="/properties" className="btn btn-large">View Properties</a>
            <a href="/contact" className="btn btn-large" style={{ backgroundColor: '#6c757d' }}>Contact Us</a>
          </div>
        </div>
      </section>
    </div>
  );
}

export default AboutUs;
